import { FileDown, Pencil, Trash2, Bell } from "lucide-react";
import { isoToEU } from "../../../lib/format";
import { downloadAgreementPdf } from "../../../lib/downloadPdf";
import { SourcePill, PaymentPill, StatusSelect } from "./BookingBadges";

/**
 * Reservations table. Pure — parent owns bookings state, filters, and
 * action handlers. Renders headers + rows + empty/loading states.
 */
export default function BookingsTable({
  bookings, loading, onStatusChange, onEdit, onDelete, onReviewModification,
}) {
  return (
    <div className="bg-white dark:bg-[#1F2937] border border-black/10 dark:border-white/10 rounded overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-[10px] uppercase tracking-[0.18em] text-neutral-500 dark:text-neutral-400 border-b border-black/8 dark:border-white/10 bg-[#FAF9F6] dark:bg-[#111827]">
            <tr>
              <th className="py-3 px-4 font-medium">Booking</th>
              <th className="py-3 px-4 font-medium">Customer</th>
              <th className="py-3 px-4 font-medium">Vehicle</th>
              <th className="py-3 px-4 font-medium">Dates</th>
              <th className="py-3 px-4 font-medium">Total</th>
              <th className="py-3 px-4 font-medium">Source</th>
              <th className="py-3 px-4 font-medium">Payment</th>
              <th className="py-3 px-4 font-medium">Status</th>
              <th className="py-3 px-4 font-medium">PDF</th>
              <th className="py-3 px-4 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={10} className="py-10 text-center text-neutral-500 dark:text-neutral-400">Loading…</td></tr>
            )}
            {!loading && bookings.length === 0 && (
              <tr><td colSpan={10} className="py-10 text-center text-neutral-500 dark:text-neutral-400">No bookings match.</td></tr>
            )}
            {!loading && bookings.map((b) => (
              <BookingRow
                key={b.id}
                b={b}
                onStatusChange={onStatusChange}
                onEdit={onEdit}
                onDelete={onDelete}
                onReviewModification={onReviewModification}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BookingRow({ b, onStatusChange, onEdit, onDelete, onReviewModification }) {
  return (
    <tr
      className="border-b border-black/5 dark:border-white/5 hover:bg-black/[0.02] dark:hover:bg-white/[0.03]"
      data-testid={`row-${b.id}`}
    >
      <td className="py-3 px-4 font-mono text-xs">{b.id.slice(0, 8).toUpperCase()}</td>
      <td className="py-3 px-4">
        <div className="font-medium">{b.full_name}</div>
        <div className="text-xs text-neutral-500 dark:text-neutral-400">{b.user_email}</div>
      </td>
      <td className="py-3 px-4">{b.vehicle_snapshot?.brand} {b.vehicle_snapshot?.name}</td>
      <td className="py-3 px-4 text-xs">
        <div>{isoToEU(b.pickup_date)}</div>
        <div className="text-neutral-500 dark:text-neutral-400">→ {isoToEU(b.return_date)}</div>
      </td>
      <td className="py-3 px-4 tabular-nums">€{b.total.toFixed(2)}</td>
      <td className="py-3 px-4">
        <SourcePill source={b.source} testId={`source-${b.id}`} />
      </td>
      <td className="py-3 px-4">
        <PaymentPill paymentStatus={b.payment_status} paymentMethod={b.payment_method} testId={`pay-${b.id}`} />
      </td>
      <td className="py-3 px-4">
        <StatusSelect
          status={b.status}
          onChange={(next) => onStatusChange(b, next)}
          testId={`status-${b.id}`}
        />
      </td>
      <td className="py-3 px-4">
        <button
          type="button"
          onClick={() => downloadAgreementPdf(b.id)}
          className="inline-flex items-center gap-1 text-xs text-neutral-600 dark:text-neutral-300 hover:text-black dark:hover:text-white"
          data-testid={`pdf-${b.id}`}
        >
          <FileDown size={12} /> PDF
        </button>
      </td>
      <td className="py-3 px-4">
        <div className="flex justify-end gap-1">
          {b.status === "modification_requested" && (
            <button
              type="button"
              onClick={() => onReviewModification?.(b)}
              aria-label={`Review modification for ${b.id.slice(0, 8)}`}
              title="Review modification request"
              data-testid={`review-modification-${b.id}`}
              className="p-1.5 rounded border border-amber-300 text-amber-700 hover:bg-amber-50 dark:border-amber-700 dark:text-amber-300 dark:hover:bg-amber-900/30"
            >
              <Bell size={14} />
            </button>
          )}
          <button
            type="button"
            onClick={() => onEdit(b)}
            aria-label={`Edit booking ${b.id.slice(0, 8)}`}
            title="Edit booking"
            data-testid={`edit-booking-${b.id}`}
            className="p-1.5 rounded border border-transparent hover:border-black/15 dark:hover:border-white/15 text-neutral-600 dark:text-neutral-300"
          >
            <Pencil size={14} />
          </button>
          <button
            type="button"
            onClick={() => onDelete(b)}
            aria-label={`Delete booking ${b.id.slice(0, 8)}`}
            title="Delete booking"
            data-testid={`delete-booking-${b.id}`}
            className="p-1.5 rounded border border-transparent hover:border-rose-300 dark:hover:border-rose-900 text-rose-600 dark:text-rose-400"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </td>
    </tr>
  );
}
